import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Logo } from "../components/Logo";
import { PasswordStrength } from "../components/PasswordStrength";
import { useI18n } from "../lib/i18n";
import { useSession } from "../lib/session";
import { ApiError, auth } from "../lib/api";

const MIN_PASSWORD = 8;

function Signup() {
  const { t, locale } = useI18n();
  const { state, refresh } = useSession();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errs, setErrs] = useState<{
    form?: string;
    email?: string;
    password?: string;
    confirm?: string;
    name?: string;
  }>({});

  if (state === "signed") {
    void nav({ to: "/app", replace: true });
    return null;
  }

  function localValidate(): boolean {
    const next: typeof errs = {};
    if (!name.trim()) next.name = t("auth.signup.displayNameRequired");
    if (password.length < MIN_PASSWORD) next.password = t("auth.signup.passwordShort");
    if (password !== confirm) next.confirm = t("auth.signup.passwordMismatch");
    setErrs(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!localValidate()) return;
    setSubmitting(true);
    setErrs({});
    try {
      // Pass detected locale + browser timezone so the user row is created
      // with the same language they're seeing — keeps the welcome email in
      // the matching language out of the gate.
      await auth.register({
        email: email.trim().toLowerCase(),
        password,
        display_name: name.trim(),
        locale,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      // Auto-login after register so the user lands authenticated.
      await auth.login({ email: email.trim().toLowerCase(), password });
      await refresh();
      void nav({ to: "/onboarding", replace: true });
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 409) {
        setErrs({ email: t("auth.signup.emailTaken") });
      } else if (e2 instanceof ApiError && e2.status === 400) {
        // Server returned a structured 400 — surface its message rather
        // than the generic fallback, since it's the authoritative reason.
        setErrs({ password: e2.message });
      } else {
        setErrs({ form: t("common.errorGeneric") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Show the mismatch error live (after the user has started typing the
  // confirmation) without flashing on every keystroke before they've
  // entered the original password.
  const liveConfirmError =
    confirm.length > 0 && password.length > 0 && confirm !== password
      ? t("auth.signup.passwordMismatch")
      : null;

  return (
    <main className="min-h-full grid place-items-center px-4 py-12">
      <div className="w-full max-w-md flex flex-col gap-6">
        <div className="flex justify-center"><Logo size="lg" /></div>
        <Card>
          <h1 className="font-display text-2xl font-semibold mb-1">{t("auth.signup.title")}</h1>
          <p className="text-secondary text-sm mb-6">{t("auth.signup.subtitle")}</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <Input
              label={t("auth.signup.displayName")}
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              errorText={errs.name ?? null}
            />
            <Input
              label={t("auth.signup.email")}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              errorText={errs.email ?? null}
            />
            <div className="flex flex-col gap-2">
              <Input
                label={t("auth.signup.password")}
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                hint={t("auth.signup.passwordHint")}
                errorText={errs.password ?? null}
              />
              <PasswordStrength
                password={password}
                labels={{
                  weak: t("auth.signup.strength.weak"),
                  fair: t("auth.signup.strength.fair"),
                  good: t("auth.signup.strength.good"),
                  strong: t("auth.signup.strength.strong"),
                }}
              />
            </div>
            <Input
              label={t("auth.signup.confirmPassword")}
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              errorText={errs.confirm ?? liveConfirmError}
            />
            {errs.form ? <p className="text-error text-sm" role="alert">{errs.form}</p> : null}
            <Button type="submit" loading={submitting} full>{t("auth.signup.submit")}</Button>
          </form>

          <p className="text-secondary text-sm mt-6 text-center">
            <Link to="/login" className="ring-nexus rounded hover:text-primary underline-offset-4 hover:underline">
              {t("auth.signup.toLogin")}
            </Link>
          </p>
        </Card>
      </div>
    </main>
  );
}

export const Route = createFileRoute("/signup")({
  component: Signup,
});
